import React, { useState } from 'react';
import {
    Typography,
    styled,
    Button,
    CircularProgress,
    Chip,
    useTheme,
} from '@mui/material';
import { translateBlock } from 'i18n';
import { useTranslation } from 'react-i18next';
import Flex from 'ui/Flex/Flex';
import EditIcon from '@mui/icons-material/Edit';
import { SelectionBaseT } from 'business/definitions';
import { MAX_CHIP_TO_DISPLAY } from '../../helpers/blockValues';
import MoreValuesChip from '../ui/MoreValuesChip';
import { getIsParentSelected } from '../../helpers/chip';
import PermissionValuesModal from './PermissionValuesModal';
import { BlockValueRemover, BlockValuesUpdater } from '../../hooks/blockValues';

const StyBlockEditorWrapper = styled(Flex, { name: 'StyBlockEditorWrapper' })(
    ({ theme }) => ({
        backgroundColor: theme.palette.primary.background,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(2),
    }),
);

const StyAddBlockButton = styled(Button, { name: 'StyButton' })({
    width: '200px',
    borderRadius: '64px',
});

interface PermissionValuesEditorProps {
    field: string;
    selectedValues: SelectionBaseT[];
    tree: TreeT | null;
    onSubmit: BlockValuesUpdater;
    onDelete: BlockValueRemover;
    isLoading: boolean;
}

const PermissionValuesEditor = ({
    field,
    selectedValues,
    onSubmit,
    onDelete,
    tree,
    isLoading,
}: PermissionValuesEditorProps) => {
    const { t } = useTranslation();
    const translatedField = translateBlock(field);
    const theme = useTheme();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const addOrUpdateButtonTitle = t(
        `adminSpace.createUser.modalStepper.scopeSection.${
            selectedValues.length === 0 ? 'addBlockValues' : 'updateBlockValues'
        }`,
    );
    const displayedValues = selectedValues
        .slice(0, MAX_CHIP_TO_DISPLAY)
        .filter(
            (resource) => !getIsParentSelected(tree, selectedValues, resource),
        );
    const shouldHideChips =
        displayedValues.length > MAX_CHIP_TO_DISPLAY ||
        selectedValues.length > displayedValues.length;

    return (
        <>
            <StyBlockEditorWrapper flexDirection="column" gap={2}>
                <Typography variant="body1SemiBold">
                    {translatedField}
                </Typography>
                <StyAddBlockButton
                    variant="outlined"
                    color="primary"
                    startIcon={
                        isLoading ? (
                            <CircularProgress
                                size={theme.spacing(3)}
                                color="inherit"
                            />
                        ) : (
                            <EditIcon />
                        )
                    }
                    disabled={isLoading || !tree}
                    onClick={() => setIsModalOpen(true)}
                >
                    {addOrUpdateButtonTitle}
                </StyAddBlockButton>
                <Flex gap={1} flexWrap="wrap">
                    {displayedValues.map((resource) => (
                        <Chip
                            key={`permission-chip-${resource.value}`}
                            sx={{ width: 'fit-content' }}
                            color="primary"
                            label={resource.label}
                            onDelete={() => onDelete(resource, field)}
                        />
                    ))}
                    {shouldHideChips && (
                        <MoreValuesChip
                            nbExtraValues={
                                selectedValues.length - displayedValues.length
                            }
                            onClick={() => setIsModalOpen(true)}
                        />
                    )}
                </Flex>
            </StyBlockEditorWrapper>
            {isModalOpen && tree && (
                <PermissionValuesModal
                    title={translatedField}
                    show={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    tree={tree}
                    initialSelectedValues={selectedValues}
                    submit={(newValues: SelectionBaseT[]) => {
                        onSubmit(newValues, field);
                        setIsModalOpen(false);
                    }}
                />
            )}
        </>
    );
};

export default PermissionValuesEditor;
